<template>
  <div class="grid-x flex-grow">
    <div class="cell auto cell-block-y flex flex-col flex-grow">
      <div class="flex flex-col flex-grow">
        <div class="flex flex-row items-center justify-end p-4 border-b">
          <button
            type="button"
            class="flex flex-row items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium transition-colors"
            @click="openFiltersModal">
            <img src="@/assets/icons/filter.svg" alt="filter" class="w-4 h-4" />
            Фильтры
            <span v-if="regionId" class="ml-1 text-primary">(1)</span>
          </button>
        </div>

        <div class="flex-grow">
          <Loading :loading="waitPending">
            <Table
              :columns="columns"
              :rows="data"
              :row-style-class="() => 'cursor-pointer'"
              :busy="waitPending"
              @on-row-click="onRowClick"
              @on-sort-change="($event) => { orderBy = $event }"/>
          </Loading>
        </div>

        <div class="flex flex-row items-end justify-center p-6">
          <Pagination
            :page="page"
            :page-count="pageCount"
            @change="onPageChange"/>
        </div>
      </div>
    </div>

    <Modal
      :id="filtersModalId"
      :hide-footer="true"
      :hide-header-close="true">
      <template v-slot:modal-title>
        <div class="flex items-center gap-2 justify-between">
          <span class="text-xl">Фильтры</span>
          <button class="size-[22px]" @click="closeFiltersModal">
            <img src="@/assets/icons/modal-header-close.svg" alt="" />
          </button>
        </div>
      </template>

      <div class="flex flex-col gap-6 p-6">
        <div class="flex flex-col gap-2">
          <span class="block text-sm font-medium">Регион работы</span>
          <Select
            :value="tempSelectedRegion"
            :options="regionFilterOptions"
            label="label"
            :reduce="(option) => option.value"
            placeholder="Выберите регион"
            :clearable="true"
            :searchable="true"
            @input="onTempRegionFilterChange"
          />
        </div>

        <div class="flex flex-row items-center justify-end gap-4">
          <button
            type="button"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
            @click="resetTempFilters">
            Сбросить
          </button>

          <button
            type="button"
            class="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded text-sm font-medium transition-colors"
            @click="applyFilters">
            Применить
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapGetters, mapActions, mapState } from 'vuex';
import Table from '@/components/Table.vue';
import Pagination from '@/components/Pagination.vue';
import Loading from '@/components/Loading.vue';
import Select from '@/components/Select.vue';
import Modal from '@/components/Modal.vue';

export default {
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  components: {
    Table,
    Pagination,
    Loading,
    Select,
    Modal,
  },
  data() {
    return {
      limit: 100,
      page: 1,
      orderBy: [],
      regionId: null,
      tempRegionId: null,
      query: '',
    };
  },
  computed: {
    ...mapGetters({
      payload: 'contractors/requests',
    }),
    ...mapState({
      workingRegions: (state) => state.settings.workingRegions,
    }),
    columns() {
      return [
        {
          label: '№',
          field: 'id',
          tdClass: 'font-bold',
          sortable: true,
        },
        {
          label: 'Регистрация',
          field: 'registrationDate',
          cell: ({ row }) => <b>
            { this.$moment(row.registrationDate, 'YYYY-MM-DD').format('DD.MM.YYYY') }
          </b>,
          sortable: true,
        },
        {
          label: 'Компания',
          field: 'name',
          sortable: true,
        },
        {
          label: 'Телефон',
          field: 'phone',
          cell: ({ row }) => <span>
            { _.get(row, 'phone') || '' }
          </span>,
          sortable: true,
        },
        {
          label: 'Регион',
          field: 'region',
          cell: ({ row }) => <span>
            { this.formatRegion(row.region) }
          </span>,
          sortable: true,
        },
        {
          label: 'Город',
          field: 'city',
          cell: ({ row }) => <span>
            { _.get(row, 'city') || '' }
          </span>,
          sortable: false,
        },
        {
          label: 'Комментарий',
          field: 'registrationComment',
          width: '360px',
          cell: ({ row }) => <span style={{
            display: 'block',
            maxWidth: '360px',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}>
            { _.get(row, 'registrationComment') || '' }
          </span>,
          sortable: false,
        },
      ];
    },
    offset() {
      return (this.page - 1) * this.limit;
    },
    data() {
      return _.get(this.payload(this.type), 'payload') || [];
    },
    total() {
      return _.get(this.payload(this.type), 'total') || null;
    },
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    queryPayload() {
      switch (this.type) {
        case 'registration':
          return {
            registered: 0,
            verified: 0,
          };
        case 'change':
          return {
            registered: 1,
            verified: 0,
          };
        default:
          return {
            registered: 1,
            verified: 1,
          };
      }
    },
    waitKey() {
      return `fetch.requests.${this.type}`;
    },
    waitPending() {
      return this.$wait.is(this.waitKey);
    },
    filtersModalId() {
      return `requests-filters-${this.type}`;
    },
    regionFilterOptions() {
      if (!this.workingRegions || !this.workingRegions.length) {
        return [];
      }

      return this.workingRegions.map((region) => ({
        value: region.id,
        label: this.formatRegion(region),
      }));
    },
    selectedRegion() {
      if (!this.regionId) return null;
      return this.regionFilterOptions.find((option) => option.value === this.regionId) || null;
    },
    tempSelectedRegion() {
      if (!this.tempRegionId) return null;
      return this.regionFilterOptions.find((option) => option.value === this.tempRegionId) || null;
    },
  },
  methods: {
    ...mapActions({
      fetch: 'contractors/ACT_FETCH_REQUESTS',
      fetchWorkingRegions: 'settings/ACT_FETCH_WORKING_REGIONS',
    }),
    formatRegion(region) {
      return [
        _.get(region, 'country.name'),
        _.get(region, 'name'),
      ].filter(Boolean).join(', ');
    },
    onPageChange(event) {
      this.page = event;
    },
    fetchPayload() {
      let { orderBy } = this;

      if (_.isEqual(orderBy, [])) {
        orderBy = [
          { column: 'id', descending: 1 },
        ];
      }

      const params = {
        key: this.type,
        offset: this.offset,
        limit: this.limit,
        orderBy,
        search: this.query,
        ...this.queryPayload,
      };

      if (this.regionId) {
        params.regionId = this.regionId;
      }

      this.fetch(params);
    },
    onTempRegionFilterChange(value) {
      this.tempRegionId = value || null;
    },
    resetTempFilters() {
      this.tempRegionId = null;
      this.regionId = null;
      this.page = 1;
      this.closeFiltersModal();
    },
    applyFilters() {
      this.regionId = this.tempRegionId;
      this.page = 1;
      this.closeFiltersModal();
    },
    openFiltersModal() {
      this.tempRegionId = this.regionId;
      this.$bvModal.show(this.filtersModalId);
    },
    closeFiltersModal() {
      this.$bvModal.hide(this.filtersModalId);
    },
    onRowClick({ row: event }) {
      this.$router.push({
        name: 'contractors.details.edit',
        params: {
          id: event.id,
        },
      });
    },
  },
  mounted() {
    this.fetchWorkingRegions().then(() => {
      this.$nextTick().then(() => {
        this.fetchPayload();
      });
    });

    this.$watch((that) => [that.page, that.orderBy, that.regionId, that.query], () => {
      this.fetchPayload();
    });
    this.$eventBus.$on('find.contractor.request', (data) => {
      this.page = 1;
      this.query = data;
    });
  },
  beforeDestroy() {
    this.$eventBus.$off('find.contractor.request');
  },
};
</script>
